import React, { FC } from 'react';
import { useIntl } from 'react-intl';
import { Label } from 'reactstrap';
import { type ApiGroup } from 'views/Widgets/Filters/AdvancedSpecializedFilter/types';
import AdminScopeDisclosure from './AdminScopeDisclosure';

type Props = {
  adminTargetAudience?: ApiGroup;
  // Defaults to true: the create modal's viewer is always about to become
  // the owner, so it can omit this prop entirely. The full-page editor
  // passes it explicitly, since its viewer may be an administrator rather
  // than the owner. Picks between two full messages below - mirrors
  // Calibration's own owner-vs-non-owner wording split (CalibrationViewModal
  // admin_filter_owner / admin_filter).
  viewerIsOwner?: boolean;
};

// Shared label for the survey-level "Target audience" field, used by both the
// create modal and the full-page editor - identical except for where the
// admin scope value comes from (a live preview vs. the survey's own frozen
// field) and which of the two help messages applies.
const TargetFilterLabel: FC<Props> = ({
  adminTargetAudience,
  viewerIsOwner = true,
}) => {
  const { formatMessage } = useIntl();

  return (
    <>
      <Label>
        {formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.editor.target_filter_label',
          defaultMessage: 'Target audience',
        })}
      </Label>
      <div className="mb-1 form-text text-muted">
        {viewerIsOwner
          ? formatMessage({
              id: 'app.views.administration.custom_surveys.surveys.editor.target_filter_help_owner',
              defaultMessage:
                'Leave empty to send to all eligible people. You can only add people within your HR administration scope in Confirm.',
            })
          : formatMessage({
              id: 'app.views.administration.custom_surveys.surveys.editor.target_filter_help_admin',
              defaultMessage:
                "Leave empty to send to all eligible people. You can only add people within the survey owner's HR administration scope in Confirm.",
            })}
        <AdminScopeDisclosure value={adminTargetAudience} />
      </div>
    </>
  );
};

export default React.memo(TargetFilterLabel);
